/**
 * DXF HEADER persistence for AutoCAD document-level system variables
 * ($INSUNITS / $PDMODE / $PDSIZE / $MIRRTEXT / $CECOLOR / $FILLMODE) — Batch-6
 * S-HEADER-INSUNITS + S-HEADER-PDMODE-PDSIZE; $MIRRTEXT added by the MIRRTEXT
 * slice (same generic int16/group-70/default-0 codec); $CECOLOR added by the
 * CECOLOR slice (group 62, int, default 256 — the union↔int conversion lives at
 * the drawing-model bridge, this module carries the raw int); $FILLMODE added
 * by the wide-polyline fill slice (group 70, int, default 1 — the Autodesk
 * documented initial value).
 *
 * AutoCAD writes these sysvars into the HEADER section as alternating
 * `9 $NAME` + `<code> <value>` pairs:
 *
 *   $INSUNITS — int16 (group 70) — drawing insertion units (0 = Unitless,
 *               1 = Inches, 4 = Millimeters, 6 = Meters, …; full table in
 *               drawing-model/blockUnits.ts). Drives Block Unit Factor auto-
 *               scaling on INSERT from another drawing. Default 0.
 *   $PDMODE   — int16 (group 70) — point display mode bitfield (0 = single
 *               dot, 2 = +, 3 = X, 4 = vertical tick; +32 circle, +64 square).
 *               Default 0.
 *   $PDSIZE   — float (group 40) — point display size. 0 = 5% of viewport
 *               height; positive = absolute units; negative = % of viewport.
 *               Default 0.
 *
 * Read/write/patch mechanics are shared with the linetype-scale vars via
 * `headerVarCodec.ts` (the generic numeric HEADER-var codec); this module owns
 * only the typed object and the var-table. The store arm lifts these into
 * typed `DrawingDocument` fields (`doc.insunits` / `doc.pdmode` / `doc.pdsize`);
 * the point-marker render arm for PDMODE/PDSIZE is DEFUSE-owned and out of
 * scope here.
 *
 * AutoCAD reference: drawing system-variable reference 2018+.
 */

import {
  patchNumericVarsIntoHeaderGroups,
  readNumericVarsFromHeader,
  writeNumericVarsToHeaderLines,
  type HeaderVarSpec,
} from "./headerVarCodec";
import type { DxfGroup } from "./types";

// ── Public types ─────────────────────────────────────────────────────────

/**
 * Decoded values for the document-level HEADER variables. Every field is
 * optional so a legacy DXF without them yields an empty object — the bridge
 * layer applies the schema defaults (`insunits: 0`, `pdmode: 0`, `pdsize: 0`).
 */
export type HeaderDocumentVars = {
  /** $INSUNITS — group 70 — drawing insertion units (0-21). */
  insunits?: number;
  /** $PDMODE — group 70 — point display mode bitfield. */
  pdmode?: number;
  /** $PDSIZE — group 40 — point display size. */
  pdsize?: number;
  /**
   * $MIRRTEXT — group 70 (int16) — MIRROR text-handling switch. 0 = text
   * stays readable when mirrored (AutoCAD default); 1 = text mirrors fully.
   * Same int16/group-70/default-0 encoding as $INSUNITS/$PDMODE.
   */
  mirrtext?: number;
  /**
   * $CECOLOR — group 62 (int) — current-entity-colour system variable, the
   * colour AutoCAD assigns to NEWLY-created entities. ONE signed int that packs
   * the whole {@link EntityColor} union via AcCmEntityColor raw-color encoding
   * (256/absent = ByLayer default; 0 = ByBlock; 1..255 = ACI; negative = raw-
   * color, incl. C2 true-colour). The union↔int conversion lives at the bridge
   * (`drawing-model` `cecolorFromHeader` / `cecolorToHeader`); this transport
   * carries the raw int. Default 256 (NOT 0 — 0 is ByBlock, a real value), so a
   * standalone ByLayer is omitted on export. ezdxf `headervars.py`: `$CECOLOR`
   * = group code 62, int, default 256, DXF12-2018.
   */
  cecolor?: number;
  /**
   * $FILLMODE — group 70 (int16) — "specifies whether hatches and fills, 2D
   * solids, and wide polylines are filled in" (Autodesk system-variable
   * reference). 1 = filled (the documented INITIAL VALUE), 0 = outlines only.
   * Saved in the drawing, which is why it is a HEADER var and not a
   * registry/session sysvar. ezdxf `sections/headervars.py`: `$FILLMODE`,
   * code 70, `default=1`, DXF12-2018 — same code, same default.
   *
   * Default 1 (NOT 0 — 0 is a REAL value meaning "draw outlines"), so an
   * absent $FILLMODE materialises the filled state and a standalone filled
   * drawing is never invented a $FILLMODE on export.
   */
  fillmode?: number;
};

/**
 * Var table: field key on {@link HeaderDocumentVars} for each `$NAME`, plus the
 * DXF group code that carries its value. $INSUNITS / $PDMODE are int16 flags
 * (group 70); $PDSIZE is a float (group 40). Drives the generic reader / writer
 * / patch so the encoding stays symmetric.
 */
const VAR_FIELDS: ReadonlyArray<HeaderVarSpec<HeaderDocumentVars>> = [
  // `defaultValue: 0` — the AutoCAD default for all three. An ABSENT var is
  // only emitted when non-zero (so a default-0 source is never invented); a var
  // ALREADY PRESENT in the header is always overwritten in-place, so an edit
  // back to 0 persists instead of preserving the stale raw value (Codex #248
  // F1). See `patchNumericVarsIntoHeaderGroups`.
  { name: "$INSUNITS", field: "insunits", code: 70, int: true, defaultValue: 0 },
  { name: "$PDMODE", field: "pdmode", code: 70, int: true, defaultValue: 0 },
  { name: "$PDSIZE", field: "pdsize", code: 40, int: false, defaultValue: 0 },
  // $MIRRTEXT — int16 (group 70), default 0. Same absent-default / overwrite-
  // in-place contract as the others (an edit back to 0 on a present var persists).
  { name: "$MIRRTEXT", field: "mirrtext", code: 70, int: true, defaultValue: 0 },
  // $CECOLOR — int (group 62), default 256 (ByLayer — distinct from the 0-default
  // siblings; 0 is a REAL value, ByBlock). A standalone ByLayer (256) is omitted
  // on export; a present $CECOLOR is overwritten in-place so an edit back to
  // ByLayer persists. The codec carries the raw raw-color int (negative C2 true-
  // colour values round-trip verbatim).
  { name: "$CECOLOR", field: "cecolor", code: 62, int: true, defaultValue: 256 },
  // $FILLMODE — int16 (group 70), default 1 (FILLED — the Autodesk documented
  // initial value, and ezdxf's `headervars.py` default for the same code).
  // Like $CECOLOR its default is NOT 0: 0 is a real state (outline-only), so
  // the absent-var default must be 1 or an outlined drawing would round-trip
  // as a filled one. Absent-and-default is never invented; a var already in
  // the HEADER is overwritten in place so an edit back to 1 persists.
  { name: "$FILLMODE", field: "fillmode", code: 70, int: true, defaultValue: 1 },
];

// ── Read ─────────────────────────────────────────────────────────────────

/**
 * Scan a HEADER section's flat group list and extract the document-level
 * sysvars. Delegates to the shared numeric-var codec; unknown variables and
 * name matches with an unexpected value code are ignored.
 */
export function readDocumentVarsFromHeader(
  headerGroups: ReadonlyArray<DxfGroup>,
): HeaderDocumentVars {
  return readNumericVarsFromHeader(headerGroups, VAR_FIELDS);
}

// ── Write ────────────────────────────────────────────────────────────────

/**
 * Produce the DxfGroup pairs for the document-level HEADER variables.
 * Symmetric with `readDocumentVarsFromHeader` — round-trip safe. Only fields
 * present on the input are emitted.
 */
export function writeDocumentVarsToHeaderLines(
  vars: HeaderDocumentVars,
): DxfGroup[] {
  return writeNumericVarsToHeaderLines(vars, VAR_FIELDS);
}

// ── Patch ────────────────────────────────────────────────────────────────

/**
 * Splice document-level variable entries into an existing HEADER section group
 * array: replace the value group following each matching `9 $NAME` entry, then
 * append any vars absent from the original section (canonical order). Every
 * other HEADER variable is preserved verbatim, keeping the file as close to the
 * original AutoCAD output as possible.
 */
export function patchDocumentVarsIntoHeaderGroups(
  existingGroups: ReadonlyArray<DxfGroup>,
  vars: HeaderDocumentVars,
): DxfGroup[] {
  return patchNumericVarsIntoHeaderGroups(existingGroups, vars, VAR_FIELDS);
}
